import * as zod from 'zod';

import type { RoutineRunnerState } from '@sb/routine-runner';
import {
  RobotControlModeKindEnum,
  BrakesStateStateEnum,
} from '@sb/sb-robot-sdk/src/codegen/typescript-axios';

export const WebsocketConnectionStatusZod = zod.discriminatedUnion('kind', [
  zod.object({ kind: zod.literal('constructing') }),
  zod.object({ kind: zod.literal('connecting') }),
  zod.object({ kind: zod.literal('connected') }),
  zod.object({ kind: zod.literal('disconnected') }),
  zod.object({ kind: zod.literal('failure'), error: zod.string() }),
]);

export type WebsocketConnectionStatus = zod.infer<
  typeof WebsocketConnectionStatusZod
>;

export const EquipmentConfig = zod.array(
  zod.object({
    id: zod.string(),
    is_enabled: zod.boolean(),
    kind: zod.string(),
    name: zod.string(),
    // Cannot use DeviceConfiguration here because there is a circular dependency:
    // DeviceConfiguration -> DeviceKind -> DeviceConfigurationRegistry. And that contains the StandardBotsRO1Configuration.
    // For some reason, not able to cleanly import this into the state.
    // So we use a generic record type instead.
    config: zod.preprocess(
      (value) => {
        if (typeof value === 'string') {
          return JSON.parse(value);
        }

        return value;
      },
      zod.record(zod.string(), zod.any()),
    ),
  }),
);

export const StandardBotsRO1State = zod.object({
  kind: zod.literal('StandardBotsRO1'),
  alias: zod.string().optional(),
  botId: zod.string().optional(),
  controlMode: zod.nativeEnum(RobotControlModeKindEnum).optional(),
  connected: zod.boolean(),
  healthy: zod.boolean(),
  build: zod.string().optional(),
  recoveryNeeded: zod.boolean().default(false),
  recoveryReason: zod.string().optional(),
  recoveryType: zod.string().optional(),
  recoveryKind: zod.string().optional(),
  brakesState: zod.nativeEnum(BrakesStateStateEnum).optional(),
  equipmentConfig: EquipmentConfig.optional(),
  routineRunnerState: zod.custom<RoutineRunnerState>().optional(),
  websocketConnectionStatus: WebsocketConnectionStatusZod.optional(),
});

export type StandardBotsRO1State = zod.infer<typeof StandardBotsRO1State>;
